import {
	CAMPAIGN_CONFIRM_SKIP_REASONS,
	CampaignConfirmResult,
	CampaignConfirmSkipReason,
} from "@signalist/shared-types";

import { ROUTING_I18N } from "./campaignRoutingFields";
import { RoutingTranslate } from "./campaignRoutingText";
import { ClientConfirmSkipReason } from "./confirmEligibility";

/** The translation key of a skip reason: one per member of the shared enum. */
export const skipReasonKey = (reason: CampaignConfirmSkipReason): string => {
	switch (reason) {
		case "NO_CAMPAIGN":
		case "NOT_INCLUDED":
		case "ALREADY_ENROLLED":
		case "CAMPAIGN_NOT_USABLE":
		case "PLACEHOLDER":
		case "FAILED":
			return `${ROUTING_I18N}.confirm.skipReasons.${reason}`;
		default: {
			const unknownReason: never = reason;

			throw new Error(`Unknown confirm skip reason ${String(unknownReason)}`);
		}
	}
};

/**
 * The message after "Confirm preassigned campaigns", built from the server's answer (root CLAUDE.md
 * §"A control names what it will do"): how many it confirmed, then each skip reason with its count,
 * in the contract's order. Never the selection's size.
 */
export const confirmResultMessage = (
	result: CampaignConfirmResult,
	t: RoutingTranslate
): string => {
	const confirmed = Number(result.confirmed) || 0;
	const counts = new Map<CampaignConfirmSkipReason, number>();

	result.skipped.forEach(({ reason, count }) =>
		counts.set(reason, (counts.get(reason) ?? 0) + (Number(count) || 0))
	);

	const skipped = CAMPAIGN_CONFIRM_SKIP_REASONS.filter(
		(reason) => (counts.get(reason) ?? 0) > 0
	).map((reason) => t(skipReasonKey(reason), { count: counts.get(reason) }));
	const head = t(`${ROUTING_I18N}.confirm.result`, { count: confirmed });

	return skipped.length > 0
		? `${head} ${t(`${ROUTING_I18N}.confirm.skippedIntro`)} ${skipped.join(", ")}.`
		: head;
};

/**
 * Before the commit, the selected leads the confirm will skip and why, per reason in the contract's
 * order ("3 selected leads are skipped: 2 already in a campaign, 1 without a preassigned
 * campaign."), or null when it skips none.
 */
export const skippedBeforeMessage = (
	skipped: readonly { reason: ClientConfirmSkipReason; count: number }[],
	t: RoutingTranslate
): string | null => {
	const total = skipped.reduce((sum, entry) => sum + entry.count, 0);

	if (total === 0) {
		return null;
	}

	const reasons = skipped.map(({ reason, count }) => t(skipReasonKey(reason), { count }));

	return t(`${ROUTING_I18N}.confirm.skippedBefore`, { count: total, reasons: reasons.join(", ") });
};
